import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { COLLECTIONS, titleKey, type CollectionKey } from '../shared/schema'
import { DEMO } from './lib/config'
import { signIn, signOut, updatePassword, useSession } from './lib/auth'
import { refreshAll, resetDemo, useRecords, useSyncState } from './lib/api'
import { Link, navigate, useLocation } from './lib/router'
import { relTime } from './lib/format'
import { Button, Icon, IconButton, Logo, Star, useToast } from './components/ui'
import { Dashboard } from './modules/Dashboard'
import { Propiedades } from './modules/Propiedades'
import { Clientes } from './modules/Clientes'
import { Calculadoras } from './modules/Calculadoras'
import { Documentos } from './modules/Documentos'
import { Marketing } from './modules/Marketing'
import { Ideas } from './modules/Ideas'
import { Agenda } from './modules/Agenda'
import { Academia } from './modules/Academia'
import { Tareas } from './modules/Tareas'
import { Ajustes } from './modules/Ajustes'

const NAV = [
  { to: '/', label: 'Panel', icon: 'home' },
  { to: '/propiedades', label: 'Propiedades', icon: 'building' },
  { to: '/clientes', label: 'Clientes', icon: 'users' },
  { to: '/agenda', label: 'Agenda', icon: 'calendar' },
  { to: '/marketing', label: 'Marketing', icon: 'megaphone' },
  { to: '/ideas', label: 'Ideas', icon: 'sparkle' },
  { to: '/calculadoras', label: 'Calculadoras', icon: 'calc' },
  { to: '/documentos', label: 'Documentos', icon: 'file' },
  { to: '/academia', label: 'Academia', icon: 'book' },
  { to: '/tareas', label: 'Tareas', icon: 'checksq' },
]

export function App() {
  const { session, recovery, checking } = useSession()
  if (checking) return <div className="splash"><Logo size={40} /></div>
  if (recovery) return <NewPassword />
  if (!session) return <Login />
  return <Shell />
}

/* --------------------------------- Shell --------------------------------- */

function Shell() {
  const { path } = useLocation()
  const { name } = useSession()
  const [menu, setMenu] = useState(false)
  const sync = useSyncState()
  const toast = useToast()
  const [, tick] = useState(0)
  useEffect(() => { const t = setInterval(() => tick(x => x + 1), 15000); return () => clearInterval(t) }, [])
  useEffect(() => { setMenu(false) }, [path])

  const page = (() => {
    const base = '/' + (path.split('/')[1] ?? '')
    switch (base) {
      case '/': return <Dashboard />
      case '/propiedades': return <Propiedades />
      case '/clientes': return <Clientes />
      case '/agenda': return <Agenda />
      case '/marketing': return <Marketing />
      case '/ideas': return <Ideas />
      case '/calculadoras': return <Calculadoras />
      case '/documentos': return <Documentos />
      case '/academia': return <Academia />
      case '/tareas': return <Tareas />
      case '/ajustes': return <Ajustes />
      default: return <div className="page"><h1>No encontramos esta sección</h1><Link to="/">Volver al panel</Link></div>
    }
  })()

  return (
    <div className={`shell ${menu ? 'menu-open' : ''}`}>
      <aside className="sidebar">
        <div className="side-brand"><Logo size={26} /></div>
        <div className="side-label">Espacio privado</div>
        <nav className="side-nav">
          {NAV.map(n => {
            const active = n.to === '/' ? path === '/' : path.startsWith(n.to)
            return (
              <Link key={n.to} to={n.to} className={`side-link ${active ? 'active' : ''}`}>
                <Icon name={n.icon} size={18} /><span>{n.label}</span>{active && <Star size={11} className="side-star" />}
              </Link>
            )
          })}
        </nav>
        <div className="side-foot">
          <Link to="/ajustes" className={`side-link ${path.startsWith('/ajustes') ? 'active' : ''}`}><Icon name="settings" size={18} /><span>Ajustes y conexión</span></Link>
          <div className="side-user">
            <span className={`avatar av-${name === 'Leticia' ? 'l' : 'y'}`}>{name[0]}</span>
            <div><strong>{name}</strong><small>Socia · REVELA</small></div>
            <IconButton icon="logout" label="Cerrar sesión" onClick={() => signOut()} />
          </div>
        </div>
      </aside>
      <div className="scrim" onClick={() => setMenu(false)} />
      <div className="main">
        <header className="topbar">
          <IconButton icon="menu" label="Abrir menú" className="only-mobile" onClick={() => setMenu(true)} />
          <div className="only-mobile top-logo"><Logo size={18} tagline={false} /></div>
          <GlobalSearch />
          <button type="button" className={`sync ${sync.error ? 'sync-error' : ''}`} onClick={async () => { await refreshAll(); toast('Datos actualizados desde Notion') }} title="Volver a leer los datos de Notion">
            <Icon name="refresh" size={15} className={sync.busy ? 'spin' : ''} />
            <span>{sync.error ? 'Sin conexión con Notion' : DEMO ? 'Datos de ejemplo' : `Notion · ${relTime(sync.lastSync)}`}</span>
          </button>
        </header>
        {DEMO && (
          <div className="demo-banner">
            <Star size={12} /><span><strong>Vista previa con datos de ejemplo.</strong> Los cambios quedan solo en este navegador. Conectada a Notion, todo se guarda en sus bases.</span>
            <button type="button" onClick={() => resetDemo()}>Restaurar ejemplos</button>
          </div>
        )}
        <main className="content">{page}</main>
      </div>
    </div>
  )
}

/* ---------------------------- Buscador general ---------------------------- */

const SEARCH: { col: CollectionKey; route: string; label: string; extra?: string[] }[] = [
  { col: 'propiedades', route: '/propiedades', label: 'Propiedad', extra: ['direccion', 'ciudad'] },
  { col: 'clientes', route: '/clientes', label: 'Cliente', extra: ['pais', 'email', 'telefono'] },
  { col: 'visitas', route: '/agenda', label: 'Cita' },
  { col: 'marketing', route: '/marketing', label: 'Contenido', extra: ['copy', 'pilar'] },
  { col: 'academia', route: '/academia', label: 'Academia', extra: ['categoria'] },
  { col: 'documentos', route: '/documentos', label: 'Documento' },
  { col: 'tareas', route: '/tareas', label: 'Tarea' },
]

function GlobalSearch() {
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const [idx, setIdx] = useState(0)
  const ref = useRef<HTMLDivElement>(null)
  const sets = SEARCH.map(s => ({ ...s, data: useRecords(s.col).data }))
  const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
  const results = useMemo(() => {
    const n = norm(q.trim())
    if (n.length < 2) return []
    const out: { key: string; label: string; title: string; sub: string; to: string }[] = []
    for (const s of sets) {
      const tk = titleKey(s.col)
      for (const r of s.data) {
        const hay = norm([r[tk], r.ref, ...(s.extra ?? []).map(k => r[k])].filter(Boolean).join(' '))
        if (hay.includes(n)) out.push({ key: r.id, label: s.label, title: r[tk] || 'Sin título', sub: [r.ref, r.estado].filter(Boolean).join(' · '), to: `${s.route}?abrir=${r.id}` })
      }
    }
    return out.slice(0, 12)
  }, [q, ...sets.map(s => s.data)])

  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); ref.current?.querySelector('input')?.focus() }
    }
    const c = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('keydown', k); document.addEventListener('mousedown', c)
    return () => { document.removeEventListener('keydown', k); document.removeEventListener('mousedown', c) }
  }, [])

  const go = (to: string) => { navigate(to); setQ(''); setOpen(false) }
  return (
    <div className="gsearch" ref={ref}>
      <Icon name="search" size={16} />
      <input id="global-search" type="search" placeholder="Buscar propiedades, clientes, contenido…" value={q}
        onChange={e => { setQ(e.target.value); setOpen(true); setIdx(0) }}
        onFocus={() => setOpen(true)}
        onKeyDown={e => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setIdx(i => Math.min(i + 1, results.length - 1)) }
          if (e.key === 'ArrowUp') { e.preventDefault(); setIdx(i => Math.max(i - 1, 0)) }
          if (e.key === 'Enter' && results[idx]) go(results[idx].to)
          if (e.key === 'Escape') setOpen(false)
        }}
        aria-label="Buscador general" />
      <kbd className="only-desktop">⌘K</kbd>
      {open && q.trim().length >= 2 && (
        <div className="gsearch-results">
          {results.length ? results.map((r, i) => (
            <button key={r.key} type="button" className={i === idx ? 'on' : ''} onMouseEnter={() => setIdx(i)} onClick={() => go(r.to)}>
              <span className="gs-type">{r.label}</span><span className="gs-title">{r.title}</span><span className="gs-sub">{r.sub}</span>
            </button>
          )) : <div className="gs-empty">Sin resultados para “{q}”.</div>}
        </div>
      )}
    </div>
  )
}

/* --------------------------------- Login --------------------------------- */

function AuthFrame({ children }: { children: ReactNode }) {
  return (
    <div className="auth">
      <section className="auth-brand">
        <div className="auth-brand-inner">
          <Logo size={54} />
          <p className="auth-quote">Un solo lugar para las propiedades, los clientes, el contenido y la Academia.</p>
          <ul className="auth-points">
            <li><Star size={11} /> Leticia · Propiedades con alma</li>
            <li><Star size={11} /> Yesica · Invertir desde afuera</li>
          </ul>
        </div>
      </section>
      <section className="auth-panel"><div className="auth-card">{children}</div></section>
    </div>
  )
}

function Login() {
  const [usuario, setUsuario] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [olvido, setOlvido] = useState(false)

  return (
    <AuthFrame>
      <form onSubmit={async e => {
        e.preventDefault(); setError(''); setBusy(true)
        try { await signIn(usuario, password); navigate('/') } catch (err: any) { setError(err.message) } finally { setBusy(false) }
      }}>
        <div className="eyebrow">REVELA Internal</div>
        <h1 className="auth-title">Bienvenida de nuevo</h1>
        <p className="muted">Ingresá con tu usuario de socia.</p>
        {DEMO && <p className="demo-note">Vista previa: entrá con cualquier usuario y contraseña.</p>}
        <div className="field"><label htmlFor="login-user">Usuario</label><input id="login-user" type="text" autoComplete="username" autoCapitalize="none" spellCheck={false} required={!DEMO} value={usuario} onChange={e => setUsuario(e.target.value)} placeholder="yesica@revela" /></div>
        <div className="field"><label htmlFor="login-pass">Contraseña</label><input id="login-pass" type="password" autoComplete="current-password" required={!DEMO} value={password} onChange={e => setPassword(e.target.value)} /></div>
        {error && <p className="form-error" role="alert">{error}</p>}
        <Button variant="primary" type="submit" busy={busy} className="btn-block">Entrar</Button>
        <button type="button" className="link-btn" onClick={() => setOlvido(!olvido)}>Olvidé mi contraseña</button>
        {olvido && <p className="hint hint-block">Pedile a tu socia que entre a <strong>Ajustes y conexión</strong> y toque <strong>Restablecer contraseña</strong>. Después entrás con la contraseña inicial de REVELA y elegís una nueva.</p>}
      </form>
    </AuthFrame>
  )
}

function NewPassword() {
  const { name } = useSession()
  const [p1, setP1] = useState('')
  const [p2, setP2] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  return (
    <AuthFrame>
      <form onSubmit={async e => {
        e.preventDefault(); setError('')
        if (p1.length < 8) return setError('Usá al menos 8 caracteres.')
        if (p1 !== p2) return setError('Las contraseñas no coinciden.')
        setBusy(true)
        try { await updatePassword(p1); navigate('/') } catch (err: any) { setError(err.message) } finally { setBusy(false) }
      }}>
        <div className="eyebrow">Primer ingreso</div>
        <h1 className="auth-title">Hola, {name}. Elegí tu contraseña</h1>
        <p className="muted">Entraste con la contraseña inicial. Elegí una propia: solo vos la vas a saber.</p>
        <div className="field"><label htmlFor="np1">Nueva contraseña</label><input id="np1" type="password" autoComplete="new-password" value={p1} onChange={e => setP1(e.target.value)} /></div>
        <div className="field"><label htmlFor="np2">Repetila</label><input id="np2" type="password" autoComplete="new-password" value={p2} onChange={e => setP2(e.target.value)} /></div>
        {error && <p className="form-error" role="alert">{error}</p>}
        <Button variant="primary" type="submit" busy={busy} className="btn-block">Guardar contraseña</Button>
        <button type="button" className="link-btn" onClick={() => signOut()}>Salir</button>
      </form>
    </AuthFrame>
  )
}

export { COLLECTIONS }
